// COP Copilot runtime — context (CONTRACT:C3-CAPABILITY-PLUGIN.1.0).
//
// LIFTED VERBATIM from takbot services/agent-runtime/src/context.ts (only the import source
// changes: @takbot/protocol → ./types.ts). This is the CONFINEMENT-CRITICAL module: it is the
// thing that ensures the model sees only tool-RESULT PREVIEWS (describeResult) of already-cleared
// rows + the classification roll-up — NEVER a raw envelope, a sealed/decrypted payload, or a
// denied-object existence/count. Preserve it unchanged.
//
// char-budgeted context-window assembly, ported from TDFBot's rag-sidecar/real_backend.py
// generate(): numbered [n] blocks, per-row truncation to doc_trunc_chars, total budget
// ctx_budget_chars. The "documents" are TOOL RESULTS (preview + provenance + the classification
// roll-up). The SESSION MARKING is the high-water-mark of every marking seen — the TDF roll-up
// banner the FinalAnswer inherits.

import type { ContextWindowConfig, ToolCall, ToolResult } from "./types.ts";
import { DEFAULT_CONTEXT_CONFIG } from "./types.ts";

/** A flattened, model-facing view of one executed tool step (the call + its result). `index`
 *  is the inline [n] the model cites. */
export interface ContextResultBlock {
  index: number;
  call_id: string;
  capability: string;
  result_ref?: string;
  marking?: string;
  body: string;
}

/** Classification ordering for the high-water-mark roll-up. Higher index = more sensitive.
 *  Unknown markings sort just above UNCLASSIFIED (fail-toward-more-sensitive). */
const MARKING_ORDER: Record<string, number> = {
  UNCLASSIFIED: 0,
  CONFIDENTIAL: 2,
  SECRET: 3,
  "TOP SECRET": 4,
  TOPSECRET: 4,
  TOP_SECRET: 4,
};

function markingRank(marking: string): number {
  const key = marking.trim().toUpperCase();
  const level = (key.split("//")[0] ?? key).trim();
  const rank = MARKING_ORDER[level];
  if (rank !== undefined) return rank;
  return 1; // unknown but present ⇒ above UNCLASSIFIED, below CONFIDENTIAL
}

/** Fold a freshly seen marking into the running session high-water-mark. */
export function rollUpMarking(
  current: string | undefined,
  next: string | undefined,
): string | undefined {
  if (!next) return current;
  if (!current) return next;
  return markingRank(next) > markingRank(current) ? next : current;
}

/** Best-effort extraction of a result_ref + marking + a compact PREVIEW from a result body.
 *  This is the confinement seam: the model only ever sees this preview string, never the raw
 *  envelope/payload. Reads well-known keys defensively and tolerates anything missing. */
export function describeResult(result: unknown): {
  result_ref?: string;
  marking?: string;
  preview: string;
} {
  if (result === null || result === undefined) {
    return { preview: "(no result body)" };
  }
  if (typeof result !== "object") {
    return { preview: String(result) };
  }
  const r = result as Record<string, unknown>;

  const result_ref =
    typeof r.result_ref === "string"
      ? r.result_ref
      : typeof r.resultRef === "string"
        ? (r.resultRef as string)
        : undefined;

  const marking = extractMarking(r);

  const payload =
    r.preview ??
    r.rows ??
    r.value ??
    r.result ??
    r.data ??
    stripMeta(r);

  let preview: string;
  try {
    preview = JSON.stringify(payload);
  } catch {
    preview = String(payload);
  }
  return { result_ref, marking, preview };
}

/** Pull a marking string out of the well-known places a result might carry it. */
function extractMarking(r: Record<string, unknown>): string | undefined {
  const candidates: unknown[] = [
    r.marking,
    r.rollup,
    r.rollup_marking,
    r.rollupMarking,
    r.classification,
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim().length > 0) return c;
    if (c && typeof c === "object") {
      const m = (c as Record<string, unknown>).marking;
      if (typeof m === "string" && m.trim().length > 0) return m;
      const lvl = (c as Record<string, unknown>).classification_level;
      if (typeof lvl === "string" && lvl.trim().length > 0) return lvl;
    }
  }
  const prov = r.provenance;
  if (prov && typeof prov === "object") {
    const m = (prov as Record<string, unknown>).marking;
    if (typeof m === "string" && m.trim().length > 0) return m;
  }
  return undefined;
}

/** Drop envelope/meta keys so a fallback preview shows the payload, not the wrapper. */
function stripMeta(r: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(r)) {
    if (k === "result_ref" || k === "resultRef" || k === "provenance" || k === "marking") {
      continue;
    }
    out[k] = v;
  }
  return out;
}

/** Build the numbered [n] context blocks from the executed (call, result) pairs, honoring the
 *  char budget. Failed tool calls are surfaced as a short error block. Returns the blocks AND the
 *  rolled-up session marking. */
export function assembleContext(
  steps: Array<{ call: ToolCall; result: ToolResult }>,
  config: ContextWindowConfig = DEFAULT_CONTEXT_CONFIG,
): { blocks: ContextResultBlock[]; sessionMarking?: string } {
  const blocks: ContextResultBlock[] = [];
  let used = 0;
  let sessionMarking: string | undefined;
  let index = 0;

  for (const { call, result } of steps) {
    index += 1;
    let result_ref: string | undefined;
    let marking: string | undefined;
    let snippet: string;

    if (result.ok) {
      const d = describeResult(result.result);
      result_ref = d.result_ref;
      marking = d.marking;
      sessionMarking = rollUpMarking(sessionMarking, marking);
      snippet = truncate(d.preview, config.doc_trunc_chars);
    } else {
      const code = result.error?.code ?? "ERROR";
      const message = result.error?.message ?? "tool call failed";
      snippet = `ERROR ${code}: ${truncate(message, config.doc_trunc_chars)}`;
    }

    const header =
      `[${index}] ${call.capability}` +
      (result_ref ? ` (ref=${result_ref})` : "") +
      (marking ? ` [${marking}]` : "");
    const body = `${header}\n${snippet}`;

    if (used + body.length > config.ctx_budget_chars && blocks.length > 0) {
      break; // budget exhausted; keep what we have (always keep at least one block)
    }
    blocks.push({ index, call_id: call.call_id, capability: call.capability, result_ref, marking, body });
    used += body.length;
  }

  return { blocks, sessionMarking };
}

/** Render the assembled blocks into the single context string prepended to the user prompt. */
export function formatToolResultsForContext(
  steps: Array<{ call: ToolCall; result: ToolResult }>,
  config: ContextWindowConfig = DEFAULT_CONTEXT_CONFIG,
): { context: string; sessionMarking?: string; blocks: ContextResultBlock[] } {
  const { blocks, sessionMarking } = assembleContext(steps, config);
  if (blocks.length === 0) {
    return { context: "", sessionMarking, blocks };
  }
  const context = `TOOL RESULTS (cite these as [n]):\n\n${blocks
    .map((b) => b.body)
    .join("\n\n")}`;
  return { context, sessionMarking, blocks };
}

/** Truncate a string to `max` characters, marking the cut. */
function truncate(s: string, max: number): string {
  if (max <= 0) return "";
  if (s.length <= max) return s;
  return s.slice(0, max) + "…";
}
